import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "./api";

type PageResult<T> = { items: T[]; total: number };

/** Keeps filters, page boundaries, and asynchronous responses in sync. */
export function usePaginatedResource<T>(query: string, { pageSize = 50, debounceMs = 200, pollMs = 0 } = {}) {
  const [position, setPosition] = useState({ query, page: 1 });
  const page = position.query === query ? position.page : 1;
  const key = `${query}${query.includes("?") ? "&" : "?"}page=${page}&pageSize=${pageSize}`;
  const currentKey = useRef(key);
  currentKey.current = key;
  const requestId = useRef(0);
  const mounted = useRef(false);
  const [result, setResult] = useState<(PageResult<T> & { key: string; query: string; page: number; updatedAt: Date })>();
  const [failure, setFailure] = useState({ key: "", message: "" });
  const error = failure.key === key ? failure.message : "";
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; requestId.current++; };
  }, []);
  useEffect(() => { setPosition({ query, page: 1 }); }, [query]);

  const refresh = useCallback(async () => {
    if (!mounted.current || key !== currentKey.current) return;
    const id = ++requestId.current;
    const isCurrent = () => mounted.current && id === requestId.current && key === currentKey.current;
    setRefreshing(true);
    setFailure({ key, message: "" });
    try {
      const response = await api<PageResult<T> | T[]>(key);
      if (!isCurrent()) return;
      const data = Array.isArray(response) ? { items: response, total: response.length } : response;
      const lastPage = Math.max(1, Math.ceil(data.total / pageSize));
      if (page > lastPage) {
        setPosition({ query, page: lastPage });
        return;
      }
      setResult({ ...data, key, query, page, updatedAt: new Date() });
    } catch (error) {
      if (isCurrent()) setFailure({ key, message: error instanceof Error ? error.message : "Results could not be loaded." });
    } finally {
      if (isCurrent()) setRefreshing(false);
    }
  }, [key, query, page, pageSize]);

  useEffect(() => {
    const timer = setTimeout(() => void refresh(), debounceMs);
    const poll = pollMs ? setInterval(() => void refresh(), pollMs) : undefined;
    return () => { clearTimeout(timer); clearInterval(poll); requestId.current++; };
  }, [refresh, debounceMs, pollMs]);

  // Keep the last loaded page and its matching count visible during navigation.
  // Clear rows immediately when filters change, so old matches never appear as new ones.
  const visible = result?.query === query ? result : undefined;
  const items = visible?.items;
  return {
    items, total: visible?.total ?? 0, page: visible?.page ?? page, pageSize,
    setPage: (page: number) => setPosition({ query, page }),
    loading: !items, refreshing: refreshing || (!error && result?.key !== key), error, refresh,
    lastUpdated: visible?.updatedAt
  };
}
